import React from 'react';
import { Shield, Users } from "lucide-react";
import { backgroundImages } from '../constants/forgotPasswordConstants';

const ForgotPasswordLayout = ({ currentImageIndex, setCurrentImageIndex, children }) => {
  return (
    <div className="min-h-screen relative">
      {/* Full Screen Background with Slideshow */}
      <div className="absolute inset-0 z-0">
        {backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
          >
            <div
              className="w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${image.url})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 min-h-screen">
        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-12 min-h-screen">
          {/* Left Content - Hero Section */}
          <div className="col-span-7 flex flex-col justify-between p-12 xl:p-16">
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-8">
                <div className="h-20 w-20 rounded-3xl bg-gradient-to-br from-teal-50 to-cyan-600 flex items-center justify-center shadow-2xl">
                  <img src='/logo.png' alt="Logo" className="h-12 w-12" />
                </div>
                <div className="text-white">
                  <h1 className="text-4xl font-black tracking-tight">Surgix</h1>
                  <p className="text-teal-200 text-lg font-medium">Healthcare Excellence Portal</p>
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-center max-w-3xl">
              <div className="space-y-8">
                <h2 className="text-6xl xl:text-7xl font-black text-white leading-tight tracking-tight">
                  {backgroundImages[currentImageIndex].title}
                </h2>
                <p className="text-2xl xl:text-3xl text-teal-100 font-light leading-relaxed">
                  {backgroundImages[currentImageIndex].subtitle}
                </p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-16">
                <div className="group bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Shield className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="text-xl font-bold">HIPAA Compliant</h3>
                      <p className="text-white/80 text-sm">Enterprise security standards</p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Users className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="text-xl font-bold">24/7 Support</h3>
                      <p className="text-white/80 text-sm">Always here when you need us</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-12">
              <div className="flex space-x-4">
                {backgroundImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-3 rounded-full transition-all duration-500 ${index === currentImageIndex
                      ? 'w-16 bg-white shadow-lg'
                      : 'w-3 bg-white/40 hover:bg-white/70'
                      }`}
                  />
                ))}
              </div>
              <div className="text-white/60 text-sm">
                Slide {currentImageIndex + 1} of {backgroundImages.length}
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="col-span-5 flex items-center justify-center p-8">
            <div className="w-full max-w-lg">
              <div className="bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/30 p-10">
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-black text-slate-800 mb-3">Forgot Password </h2>
                  <p className="text-slate-600 text-lg">Enter your user account's verified email address and we will send you a password reset link.</p>
                </div>
                {children}
                <div className="mt-10 pt-8 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-500">
                    Secure Healthcare Portal &copy; 2025 Surgix
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden min-h-screen flex flex-col">
          <div className="relative z-20 p-6 pt-12">
            <div className="flex items-center justify-center space-x-4 mb-8">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-600 flex items-center justify-center shadow-xl">
                <img src='/logo.png' alt="Logo" className="h-12 w-12" />
              </div>
              <div className="text-white text-center">
                <h1 className="text-3xl font-black">Surgix</h1>
                <p className="text-teal-200 font-medium">Healthcare Portal</p>
              </div>
            </div>
          </div>

          <div className="relative z-20 text-center px-6 mb-8">
            <h2 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4">
              {backgroundImages[currentImageIndex].title}
            </h2>
            <p className="text-lg sm:text-xl text-white/90 font-light max-w-md mx-auto">
              {backgroundImages[currentImageIndex].subtitle}
            </p>
          </div>

          <div className="flex-1 flex items-start justify-center px-4 pb-8 relative z-20">
            <div className="w-full max-w-sm">
              <div className="bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/30 p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">Forgot Password</h3>
                  <p className="text-slate-600">Enter your user account's verified email address and we will send you a password reset link.</p>
                </div>
                {React.cloneElement(children, { isMobile: true })}
                <div className="mt-8 pt-6 border-t border-slate-200 text-center">
                  <p className="text-xs text-slate-500">
                    &copy; 2025 Surgix • Secure Portal
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-20 flex justify-center pb-8">
            <div className="flex space-x-3">
              {backgroundImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${index === currentImageIndex
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/50 hover:bg-white/80'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 left-20 w-4 h-4 bg-teal-400/30 rounded-full animate-pulse"></div>
          <div className="absolute top-1/3 right-20 w-2 h-2 bg-cyan-300/40 rounded-full animate-bounce"></div>
          <div className="absolute bottom-1/4 left-1/4 w-3 h-3 bg-white/20 rounded-full animate-ping"></div>
          <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-teal-300/50 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordLayout;
